import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, takeUntil, catchError, of, finalize } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import {
  TableColumn,
  PaginationEvent,
} from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  SearchFormData,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { StatusFilterComponent } from '@shared/components/status-filter/status-filter.component';
import { ActionButton } from '@shared/components/add-form/add-form.component';
import { OrderControllerService } from '@app/api/order-service/api/orderController.service';
import { SearchOrderEsParam } from '@app/api/order-service/model/searchOrderEsParam';
import { PageInfoOrderPageVo } from '@app/api/order-service/model/pageInfoOrderPageVo';
import { OrderPageVo } from '@app/api/order-service/model/orderPageVo';
import { Router } from '@angular/router';
import {
  formatCurrencyVND,
  formatDateTime,
} from '@app/shared/utils/date-formatter.util';
import {
  formatTimeoutMinutes,
  getStockHoldCategoryLabelKey,
  hasStockHoldExpiryDisplay,
  isStockHoldExpiryExpired,
  isStockHoldExpiringSoon,
  isStockHoldTimeoutOrder,
} from '@app/shared/utils/stock-hold-timeout.util';
import { InternalOrderConfig } from '../internal.config';
import { orderStatusListForTab } from '@shared/order-status/order-status.util';
import { PermissionService } from '@app/core/services/permission.service';

@Component({
  selector: 'app-page-index',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule,
    TagModule,
    TooltipModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    StatusFilterComponent,
  ],
  templateUrl: './page-index.html',
  styleUrl: './page-index.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageIndexComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private router = inject(Router);
  private orderService = inject(OrderControllerService);
  private http = inject(HttpClient);
  private translateService = inject(TranslateService);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Signals for reactive state management
  loading = signal(false);
  error = signal<string | null>(null);
  orders = signal<OrderPageVo[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(InternalOrderConfig.PAGINATION.defaultPageSize);

  // Local state for pagination
  rows = InternalOrderConfig.PAGINATION.defaultPageSize;
  first = 0;
  goToPageNumber = 1;

  // Permission flags - computed from PermissionService
  // Using specific submenu 'standard_sales_orders' for orders permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('orders.standard_sales_orders.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('orders.standard_sales_orders.create');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('orders.standard_sales_orders.approve');
  });

  // Configuration from config file
  statusOptions = InternalOrderConfig.STATUS_FILTER_OPTIONS;
  orderSources = InternalOrderConfig.FORM_OPTIONS.orderSources;
  salesChannels = InternalOrderConfig.FORM_OPTIONS.salesChannels;
  buyerNotesOptions = InternalOrderConfig.FORM_OPTIONS.buyerNotesOptions;
  giftOptions = InternalOrderConfig.FORM_OPTIONS.giftOptions;
  searchFormConfig: FormFieldConfig[] = [
    {
      key: 'orderNumber',
      label: 'search.form.fields.order_number',
      type: 'text',
      placeholder: 'search.form.placeholders.order_number',
    },
    {
      key: 'productName',
      label: 'search.form.fields.product_name_code',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'skuCode',
      label: 'search.form.fields.sku_code',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'recipientPhone',
      label: 'search.form.fields.recipient_phone',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'salesChannels',
      label: 'search.form.fields.sales_channels',
      type: 'select',
      options: InternalOrderConfig.FORM_OPTIONS.salesChannels.map((c) => ({
        label: c,
        value: c,
      })),
    },
    {
      key: 'buyerNickname',
      label: 'search.form.fields.buyer_nickname',
      type: 'text',
      placeholder: 'search.form.placeholders.please_enter',
    },
    {
      key: 'stockHoldTimeoutOnly',
      label: 'stock_hold_timeout.order.filter_timeout_only',
      type: 'select',
      width: '280px',
      options: [
        { label: 'common.table.status.yes', value: 'yes' },
        { label: 'common.table.status.no', value: 'no' },
      ],
      placeholder: 'stock_hold_timeout.order.filter_timeout_only',
    },
  ];

  // Check if user has any action permissions (READ or APPROVE)
  hasAnyActionPermission = computed(
    () => this.canRead() || this.canApprove(),
  );

  // Table configuration - computed to conditionally include actions column
  columns = computed((): TableColumn[] => {
    const baseColumns = InternalOrderConfig.TABLE_COLUMNS.filter(
      (col) => col.prop !== 'actions',
    );
    if (this.hasAnyActionPermission()) {
      // Get the actions column from the original config to preserve the correct label
      const actionsColumn = InternalOrderConfig.TABLE_COLUMNS.find(
        (col) => col.prop === 'actions',
      );
      if (actionsColumn) {
        return [...baseColumns, actionsColumn];
      }
      // Fallback if actions column not found in config
      return [
        ...baseColumns,
        {
          label: 'order.internal.action',
          prop: 'actions',
          width: 130,
          type: 'actions' as const,
        },
      ];
    }
    return baseColumns;
  });

  // Advanced table columns split
  defaultColumns = computed((): TableColumn[] => {
    const defaultsOrder: string[] = [
      'innerOrderNo',
      'storeName',
      'goodsQuatity',
      'orderStatus',
      'fulfillmentStatus',
      'payStatus',
    ];
    const fromConfig = (prop: string) =>
      InternalOrderConfig.TABLE_COLUMNS.find((c) => c.prop === prop) as
        | TableColumn
        | undefined;

    const cols: TableColumn[] = [];
    for (const key of defaultsOrder) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig('actions');
      if (actionsCol) cols.push(actionsCol);
    }
    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const defaultProps = new Set(this.defaultColumns().map((c) => c.prop));
    return InternalOrderConfig.TABLE_COLUMNS.filter((c) => {
      // Exclude any default column
      if (defaultProps.has(c.prop)) return false;
      // Exclude actions if not permitted
      if (c.prop === 'actions' && !this.hasAnyActionPermission()) return false;
      return true;
    });
  });

  // Primary button configuration base
  private readonly primaryButtonBase = InternalOrderConfig.PRIMARY_BUTTON;

  // Primary button configuration - create sales order hidden
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: false,
  }));

  // Secondary button configuration for column config
  secondaryButton = computed(() => ({
    label: '',
    severity: 'success' as const,
    outlined: true,
    show: true,
    icon: 'view_column',
  }));

  // Computed values
  totalPages = computed(() => Math.ceil(this.totalRecords() / this.pageSize()));
  hasNextPage = computed(() => this.currentPage() < this.totalPages());
  hasPreviousPage = computed(() => this.currentPage() > 1);

  // Get orders data
  ordersData = computed(() => this.orders());

  constructor() {
    // Component initialized
  }

  ngOnInit(): void {
    this.loadOrders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadOrders(searchData?: SearchFormData): void {
    this.loading.set(true);
    this.error.set(null);

    const searchParams: SearchOrderEsParam = {
      page: this.currentPage() - 1,
      size: this.pageSize(),
      sortBy: 'creationDate',
      ascending: false,
      businessType: 'POS',
      // Filter by the canonical business order status set for the active tab ('all' → no filter)
      orderStatusList: orderStatusListForTab(this.getActiveStatus()),
      ...(searchData && {
        ...(searchData['orderNumber'] && {
          orderNo: searchData['orderNumber'],
        }),
        ...(searchData['productName'] && {
          spuName: searchData['productName'],
        }),
        ...(searchData['skuCode'] && { skuCode: searchData['skuCode'] }),
        ...(searchData['recipientPhone'] && {
          mobile: searchData['recipientPhone'],
        }),
        ...(searchData['salesChannels'] && {
          channel: searchData['salesChannels'],
        }),
        ...(searchData['buyerNickname'] && {
          buyerNick: searchData['buyerNickname'],
        }),
      }),
    };

    this.orderService
      .orderPage1(searchParams, 'response', false, {
        httpHeaderAccept: 'application/json',
      } as any)
      .pipe(
        takeUntil(this.destroy$),
        catchError((err: any) => {
          this.error.set(
            this.translateService.instant(
              'order.internal.messages.failed_to_load_orders',
            ),
          );
          console.error('Error loading orders:', err);
          return of(null);
        }),
        finalize(() => this.loading.set(false)),
      )
      .subscribe((response: any) => {
        if (response?.body) {
          let pageContent: OrderPageVo[] = response.body.pageContent || [];
          const stockHoldTimeoutOnly =
            searchData?.['stockHoldTimeoutOnly'] === 'yes';
          if (stockHoldTimeoutOnly) {
            pageContent = pageContent.filter((order) =>
              isStockHoldTimeoutOrder(order.failureReasonDescription),
            );
          }
          this.orders.set(pageContent);
          this.totalRecords.set(
            stockHoldTimeoutOnly
              ? pageContent.length
              : response.body.totalElements || 0,
          );
          this.currentPage.set((response.body.number ?? 0) + 1);
        }
      });
  }

  // Formatter functions
  formatDateTime = formatDateTime;
  formatCurrency = formatCurrencyVND;

  // Status formatter functions using config
  getOrderStatusLabel = InternalOrderConfig.getOrderStatusLabel;
  getPaymentStatusLabel = InternalOrderConfig.getPaymentStatusLabel;
  getDeliveryStatusLabel = InternalOrderConfig.getDeliveryStatusLabel;
  getReceivingStatusLabel = InternalOrderConfig.getReceivingStatusLabel;
  // getStatusBadgeClass = InternalOrderConfig.getStatusBadgeClass;

  // Use shared status methods from InternalOrderConfig
  getStatusDisplayText = InternalOrderConfig.getStatusDisplayText;
  getStatusSeverity = InternalOrderConfig.getStatusSeverity;
  getStatusStyle = InternalOrderConfig.getStatusStyle;
  getStatusTooltip = InternalOrderConfig.getStatusTooltip;

  getStockHoldCategoryLabel(category?: string): string {
    if (!category) {
      return this.translateService.instant('stock_hold_timeout.order.no_limit');
    }
    return this.translateService.instant(getStockHoldCategoryLabelKey(category));
  }

  formatStockHoldTimeout(minutes?: number): string {
    return formatTimeoutMinutes(minutes, this.translateService);
  }

  hasStockHoldExpiryDisplay = hasStockHoldExpiryDisplay;

  getStockHoldExpirySeverity(
    expiryTime?: string,
    failureReasonDescription?: string,
  ): string {
    if (isStockHoldExpiryExpired(expiryTime, failureReasonDescription)) {
      return 'danger';
    }
    if (isStockHoldExpiringSoon(expiryTime)) {
      return 'warn';
    }
    return 'info';
  }

  getStockHoldExpiryLabel(
    expiryTime?: string,
    failureReasonDescription?: string,
  ): string {
    if (isStockHoldExpiryExpired(expiryTime, failureReasonDescription)) {
      return this.translateService.instant('stock_hold_timeout.order.expired');
    }
    if (!expiryTime) {
      return this.translateService.instant('stock_hold_timeout.order.no_limit');
    }
    if (isStockHoldExpiringSoon(expiryTime)) {
      return this.translateService.instant(
        'stock_hold_timeout.order.expiring_soon',
      );
    }
    return this.formatDateTime(expiryTime);
  }

  getFailureReasonLabel(reason?: string): string {
    if (isStockHoldTimeoutOrder(reason)) {
      return this.translateService.instant(
        'stock_hold_timeout.order.failure.stock_hold_timeout',
      );
    }
    return reason || '';
  }

  onSearch(searchData: SearchFormData): void {
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders(searchData);
  }

  onReset(): void {
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders();
  }

  onPageChange(event: PaginationEvent): void {
    const newPage = Math.floor((event.first ?? 0) / (event.rows ?? 20)) + 1;
    this.currentPage.set(newPage);
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.pageSize.set(event.rows ?? 20);
    this.loadOrders();
  }

  onGoToPage(): void {
    if (this.goToPageNumber < 1 || this.goToPageNumber > this.totalPages())
      return;

    this.currentPage.set(this.goToPageNumber);
    this.first = (this.goToPageNumber - 1) * this.pageSize();
    this.loadOrders();
  }

  /**
   * Get the active status from status options
   */
  private getActiveStatus():
    | 'all'
    | 'unfinished'
    | 'completed'
    | 'closed'
    | 'abnormal' {
    const activeStatus = this.statusOptions.find((status) => status.active);
    return (activeStatus ? activeStatus.key : 'all') as
      | 'all'
      | 'unfinished'
      | 'completed'
      | 'closed'
      | 'abnormal';
  }

  /**
   * Map order status to backend number
   * Backend constants:
   * - NO_COMPLETE = "0"
   * - COMPLETE = "1"
   * - CLOSE = "2"
   * - EXCEPTION = "3"
   * If status is 'all', return undefined to exclude from payload
   */
  onStatusChange(status: string): void {
    // Update status options
    this.statusOptions = this.statusOptions.map((option) => ({
      ...option,
      active: option.key === status,
    }));

    // Reset to first page when changing status
    this.currentPage.set(1);
    this.first = 0;
    this.loadOrders();
  }

  onCreateOrder(): void {
    if (!this.canCreate()) {
      // Show warning message if needed
      return;
    }
    this.router.navigate(['/order/internal/add']);
  }

  onSelectionChange(selection: any[]): void {
    // Handle selection change
  }

  onSelectionAll(): void {
    // Handle select all
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  onView(rowData: any): void {
    if (!this.canRead()) {
      return;
    }
    const orderId = rowData.id;
    if (orderId) {
      this.router.navigate(['/order/internal/view', orderId]);
    }
  }

  // Getter methods for template
  get dataSource$() {
    return this.ordersData;
  }

  get totalRecords$() {
    return this.totalRecords;
  }

  get loading$() {
    return this.loading;
  }

  get error$() {
    return this.error;
  }

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
